import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {AJAXResponseProductData, ANALYTICS_ACTION_FOR_SIGNAGE} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {GA4EventParamName} from '@Libraries/ga-events';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {usePosterProduct, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import styles from './publishing-smart-tv-panel.module.scss';
import {useAppDispatch} from '@/hooks';
import type {PublishingSmartTvPanelProps} from './publishing-smart-tv-panel.types';

export function PublishingSmartTvPanel({...props}: PublishingSmartTvPanelProps): ReactElement {
  const [displayCode, setDisplayCode] = useState('');
  const [copied, setCopied] = useState(false);
  const dispatch = useAppDispatch();
  window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_SIGNAGE, {[GA4EventParamName.TYPE]: 'PlayOnSmartTV'});

  const premiumProductDataForPoster = usePosterProduct();
  const premiumProductDataForPrint = usePrintProduct();

  const productUsed: AJAXResponseProductData | undefined =
    premiumProductDataForPoster && Object.keys(premiumProductDataForPoster).length > 0 ? premiumProductDataForPoster : premiumProductDataForPrint;

  useEffect(() => {
    if (displayCode === '') {
      void window.PMW.readLocal('v/generateDisplayCode', {
        orderId: productUsed?.orderId,
      })
        .then((response: string) => {
          setDisplayCode(response);
        })
        .catch(() => {
          dispatch(setErrorOnPublishPanel(true));
          dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
        });
    }
  }, [displayCode]);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);
  const copyButton = (): ReactElement => {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        delay={{show: 50, hide: 100}}
        overlay={
          copied ? (
            <Tooltip id="tooltip" className="_pointer-events-none spacing-m-b-1">
              {window.i18next.t('pmwjs_link_copied')}
            </Tooltip>
          ) : (
            <div />
          )
        }
        show={copied}
        defaultShow={undefined}
        flip={undefined}
        onHide={undefined}
        onToggle={undefined}
        popperConfig={undefined}
        target={undefined}
      >
        <Button
          isFullWidth
          type={copied ? Type.SUCCESS : Type.PRIMARY}
          size={Size.SMALL}
          iconPlacement={IconPlacement.LEFT}
          text={copied ? window.i18next.t('pmwjs_copied_share_dropdown_link') : window.i18next.t('pmwjs_copy_link')}
          icon={copied ? 'icon-check' : 'icon-link'}
          onClick={() => {
            window.PMW.copyTextToClipboard(window.PMW.util.site_url(`v/d/${displayCode}`));
            setCopied(true);
          }}
          customClasses={`flex-1 ${styles.noWrapText}`}
        />
      </OverlayTrigger>
    );
  };

  return (
    <DigitalSignageSubPanel
      panelHeading={window.i18next.t('pmwjs_smart_tv')}
      heading={window.i18next.t('pmwjs_play_on_smart_tv')}
      subheading={window.i18next.t('pmwjs_play_on_smart_tv_desc')}
      showInputBar
      onClose={props.onClose}
      inputBarIcon="icon-link"
      webpageLink={window.PMW.util.site_url(`v/d/${displayCode}`)}
      showButton
      buttonComponent={copyButton()}
    />
  );
}
